import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { GraduationCap, Target, Layers, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const STUDENT_IMAGE = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/sbwj184j_iStock-2148802894.jpg';

export default function SolutionSection() {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: GraduationCap,
      title: t.solution.feature1,
      desc: t.solution.feature1Desc,
    },
    {
      icon: Target,
      title: t.solution.feature2,
      desc: t.solution.feature2Desc,
    },
    {
      icon: Layers,
      title: t.solution.feature3,
      desc: t.solution.feature3Desc,
    },
  ];

  return (
    <section
      data-testid="solution-section"
      className="bg-[#14274B] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[#66CA9D] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.solution.overline}
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-6"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {t.solution.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/70 leading-relaxed mb-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.solution.subtitle}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="text-white/60 leading-relaxed mb-12"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr'
                ? 'Nos enseignants américains certifiés dispensent les cours en direct via Zoom, dans des classes de 4 à 8 élèves. Le programme couvre l\'ELA (English Language Arts), l\'US History, le débat et l\'écriture académique. Chaque année validée contribue à l\'obtention du U.S. High School Diploma accrédité COGNIA.'
                : 'Our certified American teachers deliver live classes via Zoom, in groups of 4 to 8 students. The program covers ELA (English Language Arts), US History, debate and academic writing. Each validated year contributes toward earning the COGNIA-accredited U.S. High School Diploma.'}
            </motion.p>

            {/* Features */}
            <div className="space-y-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  data-testid={`solution-feature-${index}`}
                  className="flex gap-5"
                >
                  <div className="w-12 h-12 bg-[#2D78F6]/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[#2D78F6]" />
                  </div>
                  <div>
                    <h3
                      className="text-lg font-semibold text-white mb-2"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      className="text-white/60 leading-relaxed"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {feature.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Internal Links */}
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/programs" className="flex items-center gap-2 text-[#2D78F6] hover:text-white transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t.hero.secondaryCta} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/difference" className="flex items-center gap-2 text-[#66CA9D] hover:text-white transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t.nav.difference} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={STUDENT_IMAGE}
                alt="Élève en classe bilingue OFA France — programme américain en ligne"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#2D78F6] hidden lg:block" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
